import { API_BASE } from '../config/api';

export interface FetchResult<T> {
  data: T;
  meta: {
    api_version: string;
    last_updated: string;
    source: string;
    note: string;
  };
  responseTime: number;
  status: number;
}

/**
 * Fetch a single endpoint. Accepts optional baseUrl for multi-candidate support.
 * Falls back to the default API_BASE (Ted's API) when no baseUrl provided.
 */
export async function fetchEndpoint<T = unknown>(
  path: string,
  baseUrl?: string,
): Promise<FetchResult<T>> {
  const base = baseUrl || API_BASE;
  const start = performance.now();
  const res = await fetch(`${base}${path}`);
  const responseTime = Math.round(performance.now() - start);

  if (!res.ok) {
    throw new Error(`API error: ${res.status} ${res.statusText} for ${path}`);
  }

  const json = await res.json();
  return {
    data: json.data as T,
    meta: json.meta,
    responseTime,
    status: res.status,
  };
}

export async function fetchRaw(
  path: string,
  baseUrl?: string,
): Promise<{ json: unknown; responseTime: number; status: number }> {
  const base = baseUrl || API_BASE;
  const start = performance.now();
  const res = await fetch(`${base}${path}`);
  const responseTime = Math.round(performance.now() - start);
  const json = await res.json();
  return { json, responseTime, status: res.status };
}

export async function fetchMultiple(
  paths: string[],
  baseUrl?: string,
): Promise<Record<string, FetchResult<unknown>>> {
  const results: Record<string, FetchResult<unknown>> = {};
  const fetches = paths.map(async (path) => {
    try {
      const result = await fetchEndpoint(path, baseUrl);
      results[path] = result;
    } catch (e) {
      console.error(`Failed to fetch ${path}:`, e);
    }
  });
  await Promise.all(fetches);
  return results;
}
